import { useApiPost } from '~/composables/useApiPost'

interface RecordViewResponse {
  views_count: number
}

export const useNewsViews = () => {
  const { getToken } = useUser()
  const fingerprintStore = useFingerprintStore()

  const viewsCount = ref<number | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const getViewHeaders = (): Record<string, string> => {
    const token = getToken()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
    return headers
  }

  /**
   * Records a single news view. Must be called from the browser only — the page
   * is served through ISR (600s), so counting inside setup() would register one
   * view per cache rebuild instead of one per real visitor.
   *
   * The server deduplicates by (news_id, user_id | fingerprint_id) and always
   * responds with the current total.
   */
  const recordView = async (newsId: number): Promise<number | null> => {
    try {
      isLoading.value = true
      error.value = null

      const token = getToken()
      const body: Record<string, unknown> = { news_id: newsId }

      if (!token) {
        if (!fingerprintStore.loaded) await fingerprintStore.initializeFingerprint()
        if (fingerprintStore.visitorId) {
          body.fingerprint_id = fingerprintStore.visitorId
        }
      }

      const { data: response, error: apiError } = await useApiPost<RecordViewResponse>(
        '/news/record-view',
        body,
        getViewHeaders()
      )

      if (apiError) throw new Error('API Error')

      if (typeof response?.views_count === 'number') {
        viewsCount.value = response.views_count
      }

      return viewsCount.value
    } catch (err: unknown) {
      console.error('recordView error:', err)
      error.value = (err as Error).message
      return null
    } finally {
      isLoading.value = false
    }
  }

  return {
    viewsCount: readonly(viewsCount),
    isLoading: readonly(isLoading),
    error: readonly(error),
    recordView
  }
}
